<template>
  <page-layout
    :title="title"
    :metaTitle="title + ' - Reports'"
    :back="dynamicPreviousRoute"
  >
    <template slot="header">
      <portal-target name="reportAction" />

      <slot name="headerActions" />

      <v-tooltip bottom v-if="customReportsEnabled">
        <template #activator="{ on }">
          <v-btn
            color="secondary"
            class="mr-2"
            :class="{ 'no-min-width': isMobile }"
            @click="openSaveDialog"
            v-on="on"
          >
            <v-icon v-if="isMobile">save</v-icon>
            <template v-else>Save</template>
          </v-btn>
        </template>
        <span>Save current filters as a custom report</span>
      </v-tooltip>

      <v-tooltip v-if="isMobile && customReportsEnabled" bottom>
        <template #activator="{ on }">
          <v-btn class="no-min-width mr-2" @click="openSavedReports" v-on="on">
            <v-icon color="appMain">format_list_bulleted</v-icon>
          </v-btn>
        </template>
        <span>View saved reports</span>
      </v-tooltip>

      <v-btn
        v-if="isMobile && reportHasControls"
        class="no-min-width mr-4"
        @click="openFiltersModal = true"
      >
        <v-icon color="appMain">filter_list</v-icon>
      </v-btn>

      <v-btn
        v-if="exportable"
        :href="exportURL"
        target="_blank"
        color="secondary"
        :class="{ 'no-min-width': isMobile }"
      >
        <v-icon v-if="isMobile">file_download</v-icon>
        <template v-else>Export</template>
      </v-btn>
      <v-btn
        target="_blank"
        color="primary"
        @click="saveImage"
        v-if="canBeImage"
        :class="{ 'no-min-width': isMobile }"
      >
        <v-icon v-if="isMobile">image</v-icon>
        <template v-else>Image</template>
      </v-btn>

      <v-btn
        @click="loadReports"
        :disabled="!canUpdate"
        color="primary"
        :class="{ 'no-min-width': isMobile }"
      >
        <v-icon v-if="isMobile">refresh</v-icon>
        <template v-else>Run Report</template>
      </v-btn>
    </template>

    <v-layout class="report-page" align-stretch>
      <div class="side-nav" v-if="showSidebar">
        <content-line
          v-if="customReportsEnabled"
          class="side-nav__item"
          text="Saved Reports"
          fontIcon
          icon="save"
          @click.native="openSavedReports"
        >
          <template #actions>
            <v-chip
              small
              label
              color="primary"
              class="count-chip light--text pl-2 pr-2"
            >
              {{ savedReportsCount }}
            </v-chip>
          </template>
        </content-line>

        <v-expansion-panels accordion v-model="filtersPanel">
          <v-expansion-panel class="content-panel" expand>
            <v-expansion-panel-header>
              <content-line text="Filters" font-icon icon="filter_list" />
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <report-controls
                :report="report"
                :canUpdate="canUpdate"
                :filters="filters"
                :locked-params="lockedParams"
                @updateInput="setFilter"
                @reload="loadReports"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <div class="content-main">
        <v-progress-linear
          indeterminate
          v-if="loading || loadingChildren"
          class="sticky-pane"
          style="z-index: 4"
        />

        <template v-if="!canUpdate">
          <v-card class="ma-4" v-if="loadReportError">
            <v-card-title>
              <h5>Error</h5>
            </v-card-title>
            <v-card-text>
              {{
                loadReportError.message || 'We were unable to load this report.'
              }}
            </v-card-text>
          </v-card>

          <v-card class="ma-4" v-if="missingFields.length">
            <v-card-title>
              <h5>Select Filters</h5>
            </v-card-title>
            <v-card-text>
              <p>Please set filters for the following required fields</p>
              <ul>
                <li v-for="field in missingFields" :key="field.param">
                  {{ field.title }}
                </li>
              </ul>
            </v-card-text>
          </v-card>
        </template>

        <template v-else>
          <component
            v-if="isUnframedVisual(report)"
            :is="visualsByType[report.visual]"
            :report="report"
            :key="report.id"
            :resources="resources"
            @reportExpanded="navigateToReport"
            @reportFiltered="reportFiltered"
            @paginate="paginate"
          >
          </component>

          <div v-else class="container" :key="report.id + 'r'">
            <v-card>
              <v-responsive :aspect-ratio="chartAspectRatio">
                <v-card-text>
                  <component
                    :is="visualsByType[report.visual] || visualsByType.chart"
                    ref="reportChart"
                    :report="report"
                    :sticky="true"
                    :resources="resources"
                    :filters="filters"
                    @reportFiltered="reportFiltered"
                  />
                </v-card-text>
              </v-responsive>
            </v-card>
          </div>
        </template>
      </div>
    </v-layout>

    <custom-report-form-dialog
      v-if="report && !sharePid"
      :report="report"
      @openSavedReports="openSavedReports"
    />

    <ReportShareModal
      v-if="showShareModal"
      :report="report"
      :customReportId="customReportId"
    />

    <v-dialog
      v-if="isMobile"
      v-model="openFiltersModal"
      max-width="480px"
      content-class="fixed-height-md"
    >
      <modal-card title="Filters" fixed @close="openFiltersModal = false">
        <report-controls
          :report="report"
          :canUpdate="canUpdate"
          :filters="filters"
          :locked-params="lockedParams"
          @updateInput="setFilter"
          @reload="loadReports"
        />

        <template v-slot:actions>
          <v-btn color="appMain" :disabled="!canUpdate" @click="applyFilters">
            Update
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>
  </page-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import clone from 'lodash/clone';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import debounce from 'lodash/debounce';
import qs from 'qs';

import { onParent } from '@/lib/parentConnection';
import { asyncForEach } from '@/lib/misc';
import EventBus from '@/factories/eventBus';

import visualsByType from './components/visualsByType';
import ReportControls from './components/ReportControls';
import CustomReportFormDialog from './components/CustomReportFormDialog';
import CampaignFilter from '@/views/campaigns/components/CampaignFilter.vue';
import ReportShareModal from '@/views/reports/components/ReportShareModal.vue';

export default {
  name: 'report',
  components: {
    ReportShareModal,
    CampaignFilter,
    ReportControls,
    CustomReportFormDialog,
  },
  props: {
    sharePid: String,
    id: String,
    initialFilters: Object,
    lockedParams: Array,
    customTitle: String,
    hideSave: Boolean,
    customReportId: String,
  },
  data() {
    return {
      filtersPanel: 0,
      visualsByType,
      unframedVisuals: [
        'table',
        'embed',
        'dashboard',
        'sentMessages',
        'funnel',
      ],
      loading: false,
      loadingChildren: false,
      openFiltersModal: false,
      filters: {},
      filtersInitialized: false,
      prevRoute: '/reports',
      resources: {
        campaign: [
          'campaigns/campaignNames',
          'campaigns/loadCampaignNames',
          '/campaigns/:id/content',
        ],
        group: ['groups', 'loadGroups'],
        groups: ['groups', 'loadGroups'],
        group_by: ['fields/all', 'fields/load'],
        fields: ['fields/all', 'fields/load'],
        field: ['fields/all', 'fields/load'],
        tracker_name: ['lessonsTrackerNames', 'loadLessonsTrackerNames'],
        tracker_names: ['lessonsTrackerNames', 'loadLessonsTrackerNames'],
        custom_object: ['customObjects/all', 'customObjects/load'],
      },
      paginationCursors: {},
      loadReportError: null,
      //used to force an update on this.filters for exportURL and others that rely
      //on filters as a whole
      serializedFilters: '',
    };
  },

  computed: {
    ...mapGetters('reports', ['byId', 'bySharePid']),
    ...mapGetters('teams', ['currentTeam']),
    ...mapGetters('customReports', ['allByClass']),

    report() {
      if (this.id) {
        return this.byId(this.id);
      }

      if (this.sharePid) {
        return this.bySharePid(this.sharePid);
      }

      return null;
    },

    exportable() {
      return this.report?.exportable || false;
    },

    title() {
      if (this.customTitle) return this.customTitle;
      const reportTitle = (this.report || {}).title || '';
      if (this.sharePid || !reportTitle) return reportTitle;
      return reportTitle;
    },

    missingFields() {
      const controls = this.report?.controls ?? {};
      return Object.values(controls).filter((control) => {
        const { rules, param } = control || {};
        return rules?.includes('required') && !this.filters[param];
      });
    },

    canUpdate() {
      if (!this.report) return false;
      return !this.missingFields.length;
    },

    exportURL() {
      if (!this.report) return '';

      const params = this.serializedFilters;
      const reportPath = this.reportPath(this.report.id);

      let path = `/api/reports/${reportPath}?${params}&format=csv`;
      if (this.currentTeam) {
        path += `&teams[]=${this.currentTeam.id}`;
      }

      return path;
    },

    dynamicPreviousRoute() {
      let route = this.prevRoute;
      const { query } = this.$route || {};

      if (!this.report?.parent) {
        if (this.customReportId) {
          return `/reports/${this.id}/records`;
        }

        //no not show back button if this is a shared report.
        if (this.sharePid) {
          //if the URL includes a backButton
          if (query?.backButton) {
            return () => onParent('back', (back) => back());
          }
          return '';
        }

        return `/reports`;
      }

      if ((this.prevRoute || '').includes('group_by') && query?.filters) {
        return route;
      }

      const params = qs.stringify(this.filters);

      route = `/reports`;
      if (this.sharePid) {
        route = `${route}/shared/${this.sharePid}`;
      } else {
        route = `${route}/${this.report.parent}`;
      }

      route += `?${params}`;

      return route;
    },

    canBeImage() {
      return ['pie', 'bar', 'line', 'bubble'].includes(this.report?.visual);
    },

    chartAspectRatio() {
      return this.isMobile ? 9 / 16 : 16 / 9;
    },

    reportHasControls() {
      const controls = this.report?.controls;
      return controls && Object.keys(controls).length;
    },

    isModalMode() {
      return !!this.initialFilters;
    },

    customReportsEnabled() {
      return (
        this.report?.controls &&
        !this.sharePid &&
        !this.hideSave &&
        !this.report?.parent
      );
    },

    savedReportsCount() {
      if (!this.report) return 0;
      return this.allByClass(this.report.id).length;
    },

    showSidebar() {
      return !this.isMobile && this.reportHasControls;
    },

    showShareModal() {
      return !this.sharePid && (this.customReportId || !this.report?.controls);
    },
  },

  watch: {
    report: {
      immediate: true,
      handler(current) {
        if (!current) {
          return this.loadReportsEventually();
        }
        this.setDefaultFilters();

        // fetch the report if we don't have it yet and we're not in share mode,
        // or if we're in share mode but the report doesn't have controls
        // (which means it won't show up in the list of saved reports to load from)
        if (!this.sharePid && !this.hideSave) {
          this.loadByClass(current.id);
        }
      },
    },

    initialFilters(val) {
      if (val && !this.filtersInitialized) {
        this.setFilters();
        this.updatePath();
        if (this.canUpdate) {
          this.loadReportsEventually();
        }
      }
    },

    $route: {
      immediate: true,
      handler(val) {
        this.checkFiltersAfterRouteChange(val);
      },
    },
  },

  methods: {
    ...mapActions('reports', ['loadOne']),
    ...mapActions('customReports', ['loadByClass']),

    applyFilters() {
      this.loadReportsEventually();
      this.openFiltersModal = false;
    },

    openSaveDialog() {
      EventBus.$emit('openCustomReportFormDialog', {
        filters: this.filters,
        title: this.title,
      });
    },

    openSavedReports() {
      const route = {
        name: 'customReportsList',
        params: { id: this.report.id },
      };

      if (this.isModalMode) {
        this.openConfirmationModal({
          title: 'Leave Report',
          text: 'Open Saved Reports? This will close the current modal.',
          buttonText: 'Continue',
          onConfirm: () => {
            this.$emit('closeModal');
            this.$router.push(route);
          },
        });
        return;
      }

      this.$router.push(route);
    },

    async checkFiltersAfterRouteChange(route) {
      if (this.isModalMode) return;

      await this.$nextTick();

      const { query } = route;
      const isFiltersChanged = !isEqual(
        omit(query, ['user_id']),
        omit(this.filters, ['user_id'])
      );

      if (isFiltersChanged) {
        this.setFilters();
        if (this.canUpdate) {
          this.loadReportsEventually();
        }
      }
    },

    isUnframedVisual(report) {
      return this.unframedVisuals.includes(report.visual);
    },

    reportPath(reportId) {
      if (!this.sharePid) {
        return reportId;
      }

      let path = `shared/${this.sharePid}`;

      if (reportId) {
        path += `/${reportId}`;
      }

      return path;
    },

    /**
     * eventually loads reports afte 300ms of interaction
     * there are so many different input settings that occur at load time and during interaction
     * We want to avoid loading reports more than we need to.
     * @param function callback
     */
    loadReportsEventually: debounce(async function (callback) {
      await this.loadReports();
      if (callback) {
        callback();
      }
    }, 300),

    async loadReports() {
      this.paginationCursors = {};

      let params = clone(this.filters);
      if (!this.missingFields.length) {
        params.data = 1;
      }

      // hack for automations report
      if (params.default_campaign_id) {
        if (!params.campaign_id) {
          params.campaign_id = params.default_campaign_id;
        }
        delete params.default_campaign_id;
      }

      this.loading = true;
      try {
        await this.loadOne({
          path: this.reportPath(this.id),
          params,
        });
        this.loadReportError = null;
      } catch (err) {
        this.loadReportError = err?.data;
      } finally {
        this.loading = false;
      }

      const childReports = this.report?.child_reports;

      if (Array.isArray(childReports) && this.canUpdate) {
        this.loadingChildren = true;
        await asyncForEach(childReports, async (report) => {
          //if the report doesn't have x, y cooridnates, it may be a "behind the scenes"
          //report that is used as a control see ReportOptionsList
          if ([report.size_x, report.size_y].includes(undefined)) return;
          const reportParams = report.params || {};
          params = {
            ...reportParams,
            ...params,
          };
          if (this.canUpdate) {
            params.data = 1;
          }
          await this.loadOne({
            path: this.reportPath(report.id),
            params,
          });
        });
        this.loadingChildren = false;
      }
    },

    async paginate(evt) {
      const report = evt.report;
      const cursors = this.paginationCursors;

      if (!cursors[report.id]) {
        cursors[report.id] = [];
      }

      let cursor = '';

      if (evt.direction < 0) {
        cursor = cursors[report.id].pop();
      } else {
        cursor = report.data.pagination.next_cursor;
        cursors[report.id].push(cursor);
      }

      //sometimes paging gets reset in ReportTable when changing table data sets
      //we want to make sure there is a cursor in history before sending query.
      if (!cursor) {
        return;
      }

      this.paginationCursors = cursors;

      this.loading = true;
      await this.loadOne({
        path: report.id,
        params: {
          data: 1,
          query_id: report.data.query_id,
          cursor,
        },
      });
      this.loading = false;
    },

    setFilter(key, value) {
      const method = value === null ? '$delete' : '$set';

      const prevValue = this.filters[key];

      //loose comparison by design
      if (prevValue !== value) {
        this[method](this.filters, key, value);
        this.updatePath();

        // In modal mode, trigger reload on filter change
        if (this.isModalMode && this.canUpdate) {
          this.loadReportsEventually();
        }
      }
    },

    updatePath: debounce(function () {
      this.serializedFilters = qs.stringify(this.filters);

      // In modal mode, just update serializedFilters for export URL
      // Loading is handled by initialReportCall and setFilter
      if (this.isModalMode) {
        return;
      }

      this.prevRoute = this.$route?.fullPath;

      let path = '';
      //still need this qs.stringify here.
      //not using .replace because that re-renders
      //this could also be used...
      // https://stackoverflow.com/questions/51337255/silently-update-url-without-triggering-route-in-vue-router
      path = window.location.pathname + '?' + this.serializedFilters;
      history.pushState(null, '', path);

      if (this.canUpdate) {
        this.loadReportsEventually();
      }

      onParent('navigate', (navigate) => navigate(path));
    }, 500),

    async loadResource(resource) {
      let getter = resource[0];
      let action = resource[1];
      let value = this.$store.getters[getter];

      if (!value) {
        return await this.$store.dispatch(action);
      }

      if (Array.isArray(value) && value.length) {
        return true;
      }

      if (typeof value === 'object' && Object.values(value).length) {
        return true;
      }

      return await this.$store.dispatch(action);
    },

    async loadResources() {
      let controls = this.report?.controls || {};

      for (let c in controls) {
        let resource = this.resources[c];
        if (!resource) {
          continue;
        }
        await this.loadResource(resource);
      }

      //load relation fields
      let fields = this.report?.fields || {};
      for (let f in fields) {
        if (fields[f].is !== 'relation' || !fields[f].resource) {
          continue;
        }
        let resource = this.resources[fields[f].resource];
        if (resource) {
          await this.loadResource(resource);
        }
      }
    },

    navigateToReport(report) {
      if (this.isModalMode) {
        this.$emit('navigateReport', {
          reportId: report.id,
          filters: this.filters,
        });
        return;
      }

      const config = {
        name: 'report',
        params: {
          id: report.id,
        },
        //qs is used under the hood in router options
        query: this.filters,
      };

      if (this.sharePid) {
        config.name = 'reportShared';
        config.params.pid = this.sharePid;
      }

      this.$router.push(config);
    },

    reportFiltered(event) {
      if ((!event && !event.key) || event.value === undefined) {
        return false;
      }
      const control = (this.report.controls || {})[event.key];
      const controlParam =
        event.key === 'filters'
          ? event.key
          : control && control.param
          ? control.param
          : '';

      if (controlParam) {
        this.setFilter(controlParam, event.value);
      }
    },

    saveImage() {
      const reportChart = this.$refs.reportChart;
      if (!reportChart) return;

      const chart = reportChart.$children.find((child) => child.chart);
      if (!chart) return;

      const img = chart.saveImage();
    },

    setFilters() {
      const filters = this.initialFilters || this.$route.query;

      //this is a hack, should be updated to format the original filters with defaults
      if (filters.user_id && !Array.isArray(filters.user_id)) {
        filters.user_id = parseInt(filters.user_id);
      }

      this.filters = { ...filters };
      if (this.initialFilters) {
        this.filtersInitialized = true;
      }
    },

    setDefaultFilters() {
      const controls = this.report.controls || {};
      const filters = this.filters;

      let defaultSet = false;
      //set defaults via controls.
      for (const key in controls) {
        const control = controls[key];
        if (
          filters[control.param] === undefined &&
          control.default !== undefined
        ) {
          filters[control.param] = this.getDefaultValue(control);
          defaultSet = true;
        }
      }

      if (isEqual(this.filters, filters)) {
        return;
      }

      this.filters = filters;
      if (defaultSet) {
        this.updatePath();
      }
    },

    getDefaultValue(control) {
      if (
        control.type === 'timeframe' &&
        this.id === 'officeScheduleDashboard'
      ) {
        return '7d';
      }

      return control.default;
    },

    initialReportCall() {
      const loadResources = this.loadResources.bind(this);

      if (!this.report || this.canUpdate) {
        return this.loadReportsEventually(loadResources);
      }

      loadResources();
    },

    initReport() {
      this.setFilters();
      this.updatePath();
      this.initialReportCall();
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name && from.path.length > 1) {
        vm.prevRoute = from.path;
      }
    });
  },

  mounted() {
    this.initReport();
  },
};
</script>

<style scoped>
.report-page {
  height: 100%;
}

.v-btn.report-filter {
  min-width: 0 !important;
}
</style>

<style lang="less" src="@/assets/styles/basic/form.less"></style>
<style lang="less" src="@/assets/styles/basic/side-nav.less"></style>
